type PortalUrlOptions = {
  slug?: string | null;
  origin?: string | null;
  verification?: string | null;
};

const configuredPortalBaseUrl = (import.meta.env.VITE_CUSTOMER_PORTAL_BASE_URL as string | undefined)?.trim() ?? '';
const configuredPortalPathPrefix = (import.meta.env.VITE_CUSTOMER_PORTAL_PATH_PREFIX as string | undefined)?.trim() ?? '';

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function normalizePathSegment(value: string): string {
  if (!value) {
    return '';
  }

  const withoutEdges = value.replace(/^\/+|\/+$/g, '');
  return withoutEdges ? `/${withoutEdges}` : '';
}

function normalizePathPrefix(): string {
  if (configuredPortalPathPrefix) {
    return normalizePathSegment(configuredPortalPathPrefix);
  }

  // Separate Portal-Domain nutzt standardmäßig die Root-Route statt `/kundenportal`.
  if (configuredPortalBaseUrl) {
    return '';
  }

  return '/kundenportal';
}

function resolvePortalBaseOrigin(origin?: string | null): string {
  const fallbackOrigin = origin?.trim() ?? '';

  if (configuredPortalBaseUrl) {
    try {
      const parsedUrl = new URL(configuredPortalBaseUrl);
      return trimTrailingSlash(parsedUrl.origin + parsedUrl.pathname);
    } catch (error) {
      console.warn('Ungültige VITE_CUSTOMER_PORTAL_BASE_URL, Fallback auf aktuelle Origin.', error);
    }
  }

  return trimTrailingSlash(fallbackOrigin);
}

function createAbsoluteUrl(pathname: string, origin?: string | null): string {
  const browserOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const baseOrigin = resolvePortalBaseOrigin(origin || browserOrigin);

  if (baseOrigin) {
    return new URL(pathname, `${baseOrigin}/`).toString();
  }

  return pathname;
}

function buildPortalPath(slug?: string | null): string {
  const pathPrefix = normalizePathPrefix();
  const normalizedSlug = slug?.trim() ? `/${slug.trim()}` : '';
  const path = `${pathPrefix}${normalizedSlug}`;

  return path || '/';
}

export function buildCustomerPortalUrl({ slug, origin, verification }: PortalUrlOptions): string {
  const resolvedUrl = createAbsoluteUrl(buildPortalPath(slug), origin);

  if (!verification) {
    return resolvedUrl;
  }

  if (!/^https?:\/\//i.test(resolvedUrl)) {
    const separator = resolvedUrl.includes('?') ? '&' : '?';
    return `${resolvedUrl}${separator}verification=${encodeURIComponent(verification)}`;
  }

  const url = new URL(resolvedUrl);

  url.searchParams.set('verification', verification);

  return url.toString();
}

export function buildCustomerPortalVacationUrl({ slug, origin }: Omit<PortalUrlOptions, 'verification'>): string {
  const normalizedSlug = slug?.trim() ? `/${slug.trim()}` : '';
  return createAbsoluteUrl(`/urlaubbeantragen${normalizedSlug}`, origin);
}
